import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Loader2, TrendingUp, Calendar, Euro, Sparkles } from "lucide-react";
import { crmSalesForecast } from "@/lib/crm.functions";

export const Route = createFileRoute("/crm/reports/monthly")({
  head: () => ({
    meta: [
      { title: "Monthly Sales & Forecast — CRM" },
      { name: "description", content: "Historical monthly sales and forward-looking revenue forecast." },
      { property: "og:title", content: "Monthly Sales & Forecast — CRM" },
      { property: "og:description", content: "Historical monthly sales and forward-looking revenue forecast." },
    ],
  }),
  component: MonthlySalesPage,
  errorComponent: ({ error }) => (
    <div className="grid min-h-screen place-items-center px-6 text-center text-sm text-muted-foreground">
      {(error as Error | undefined)?.message ?? "Failed to load report"}
    </div>
  ),
});

const STAGE_META: Record<string, { label: string; color: string }> = {
  prospekti: { label: "Prospect", color: "#64748b" },
  kontaktoitu: { label: "Contacted", color: "#0ea5e9" },
  tapaaminen: { label: "Meeting", color: "#a855f7" },
  tarjous: { label: "Proposal", color: "#f59e0b" },
  kauppa: { label: "Deal", color: "#10b981" },
};

const eur = (n: number) =>
  new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const HISTORY_OPTIONS = [3, 6, 12, 24] as const;
const FORECAST_OPTIONS = [3, 6, 12] as const;

function MonthlySalesPage() {
  const fetchReport = useServerFn(crmSalesForecast);
  const [historyMonths, setHistoryMonths] = useState<number>(12);
  const [forecastMonths, setForecastMonths] = useState<number>(6);
  const { data, isLoading, error } = useQuery({
    queryKey: ["crm-sales-forecast", { h: historyMonths, f: forecastMonths }],
    queryFn: () => fetchReport({ data: { history_months: historyMonths, forecast_months: forecastMonths } }),
  });

  if (isLoading) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 text-sm text-red-600">
        {(error as any)?.message ?? "Failed to load"}
      </div>
    );
  }

  const maxRevenue = Math.max(1, ...data.monthly.map((m) => m.revenue));
  const maxForecast = Math.max(
    1,
    ...data.forecast.map((f) => f.recurring_eur + f.committed_won_eur),
  );

  const totalHistoryRevenue = data.monthly.reduce((s, m) => s + m.revenue, 0);
  const totalHistoryDeals = data.monthly.reduce((s, m) => s + m.deals, 0);
  const avgDeal = totalHistoryDeals ? totalHistoryRevenue / totalHistoryDeals : 0;
  const next6Recurring = data.forecast.reduce((s, m) => s + m.recurring_eur, 0);
  const next6Committed = data.forecast.reduce((s, m) => s + m.committed_won_eur, 0);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 md:py-8 space-y-8">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Link
            to="/crm/reports"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Reports
          </Link>
          <h1 className="mt-1 text-xl md:text-2xl font-bold">Monthly Sales & Forecast</h1>
          <p className="text-sm text-muted-foreground">
            Past {historyMonths} months of won revenue, and the next {forecastMonths} months of expected recurring income.
          </p>
        </div>
      </div>

      {/* Range controls */}
      <div className="flex flex-wrap items-center gap-4 rounded-xl border bg-white p-3">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">History</label>
          <select
            value={historyMonths}
            onChange={(e) => setHistoryMonths(Number(e.target.value))}
            className="rounded-md border px-2 py-1 text-sm"
          >
            {HISTORY_OPTIONS.map((m) => (
              <option key={m} value={m}>Last {m} months</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Forecast</label>
          <select
            value={forecastMonths}
            onChange={(e) => setForecastMonths(Number(e.target.value))}
            className="rounded-md border px-2 py-1 text-sm"
          >
            {FORECAST_OPTIONS.map((m) => (
              <option key={m} value={m}>Next {m} months</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile icon={<Euro className="h-4 w-4" />} label={`Last ${historyMonths}M Revenue`} value={eur(totalHistoryRevenue)} />
        <SummaryTile icon={<TrendingUp className="h-4 w-4" />} label={`Deals Won (${historyMonths}M)`} value={String(totalHistoryDeals)} />
        <SummaryTile icon={<Sparkles className="h-4 w-4" />} label="Avg Deal Size" value={eur(avgDeal)} />
        <SummaryTile icon={<Calendar className="h-4 w-4" />} label={`Next ${forecastMonths}M Recurring`} value={eur(next6Recurring)} sub={next6Committed ? `+ ${eur(next6Committed)} committed` : undefined} />
      </div>

      {/* History chart */}
      <section className="rounded-2xl border bg-white p-4 md:p-6">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-base font-semibold">Monthly Sales — Last {historyMonths} Months</h2>
          <span className="text-xs text-muted-foreground">Based on deals won</span>
        </div>
        <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
          <div
            className="flex gap-2 items-end h-48 min-w-[560px] md:min-w-0"
          >
            {data.monthly.map((m) => {
              const h = (m.revenue / maxRevenue) * 100;
              return (
                <div key={m.key} className="flex-1 min-w-[38px] flex flex-col items-center gap-1.5">
                  <div className="text-[10px] font-semibold text-slate-700 truncate w-full text-center">
                    {m.revenue > 0 ? eur(m.revenue) : "—"}
                  </div>
                  <div
                    className="w-full rounded-t-md bg-emerald-500/85 hover:bg-emerald-500 transition-colors"
                    style={{ height: `${Math.max(2, h)}%` }}
                    title={`${m.label}: ${eur(m.revenue)} (${m.deals} deals)`}
                  />
                  <div className="text-[10px] text-muted-foreground truncate w-full text-center">
                    {m.label.replace(" ", "\n").split("\n")[0]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                <th className="py-2">Month</th>
                <th className="py-2 text-right">Deals Won</th>
                <th className="py-2 text-right">Revenue</th>
                <th className="py-2 text-right">Avg Deal</th>
              </tr>
            </thead>
            <tbody>
              {data.monthly.map((m) => (
                <tr key={m.key} className="border-b last:border-0">
                  <td className="py-2">{m.label}</td>
                  <td className="py-2 text-right">{m.deals}</td>
                  <td className="py-2 text-right font-medium">{eur(m.revenue)}</td>
                  <td className="py-2 text-right text-muted-foreground">
                    {m.deals ? eur(m.revenue / m.deals) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Forecast chart */}
      <section className="rounded-2xl border bg-white p-4 md:p-6">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-base font-semibold">Forecast — Next {forecastMonths} Months</h2>
          <span className="text-xs text-muted-foreground">Recurring MRR + committed wins</span>
        </div>
        <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
          <div className="flex gap-3 items-end h-48 min-w-[420px] md:min-w-0">
            {data.forecast.map((f) => {
              const total = f.recurring_eur + f.committed_won_eur;
              const h = (total / maxForecast) * 100;
              const recH = total ? (f.recurring_eur / total) * h : 0;
              const comH = total ? (f.committed_won_eur / total) * h : 0;
              return (
                <div key={f.key} className="flex-1 min-w-[52px] flex flex-col items-center gap-1.5">
                  <div className="text-[10px] font-semibold text-slate-700 truncate w-full text-center">
                    {total > 0 ? eur(total) : "—"}
                  </div>
                  <div className="w-full flex flex-col-reverse" style={{ height: "100%" }}>
                    <div
                      className="w-full bg-sky-500/80"
                      style={{ height: `${recH}%` }}
                      title={`Recurring: ${eur(f.recurring_eur)} from ${f.subscriptions} subs`}
                    />
                    {comH > 0 && (
                      <div
                        className="w-full bg-emerald-500/85 rounded-t-md"
                        style={{ height: `${comH}%` }}
                        title={`Committed won: ${eur(f.committed_won_eur)}`}
                      />
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate w-full text-center">
                    {f.label.replace(" ", "\n").split("\n")[0]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-sky-500/80" />Recurring MRR (active subscriptions)</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500/85" />Committed won deals</span>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                <th className="py-2">Month</th>
                <th className="py-2 text-right">Active Subs</th>
                <th className="py-2 text-right">Recurring €</th>
                <th className="py-2 text-right">Committed €</th>
                <th className="py-2 text-right">Total Forecast</th>
              </tr>
            </thead>
            <tbody>
              {data.forecast.map((f) => (
                <tr key={f.key} className="border-b last:border-0">
                  <td className="py-2">{f.label}</td>
                  <td className="py-2 text-right">{f.subscriptions}</td>
                  <td className="py-2 text-right">{eur(f.recurring_eur)}</td>
                  <td className="py-2 text-right">{eur(f.committed_won_eur)}</td>
                  <td className="py-2 text-right font-medium">
                    {eur(f.recurring_eur + f.committed_won_eur)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pipeline snapshot */}
      <section className="rounded-2xl border bg-white p-4 md:p-6">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-base font-semibold">Open Pipeline Snapshot</h2>
          <span className="text-xs text-muted-foreground">
            {data.pipeline.open_count} deals · {eur(data.pipeline.open_total_eur)} total ·{" "}
            {eur(data.pipeline.weighted_eur)} weighted
          </span>
        </div>
        {data.pipeline.by_stage.length === 0 ? (
          <p className="text-sm text-muted-foreground">No open deals in the pipeline.</p>
        ) : (
          <div className="space-y-2">
            {data.pipeline.by_stage
              .sort((a, b) => b.total - a.total)
              .map((row) => {
                const meta = STAGE_META[row.stage] ?? { label: row.stage, color: "#64748b" };
                const pct = (row.total / Math.max(1, data.pipeline.open_total_eur)) * 100;
                return (
                  <div key={row.stage} className="rounded-lg border p-3">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ background: meta.color }}
                        />
                        <span className="font-medium">{meta.label}</span>
                        <span className="text-muted-foreground">· {row.count} deal{row.count === 1 ? "" : "s"}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{eur(row.total)}</div>
                        <div className="text-xs text-muted-foreground">
                          weighted {eur(row.weighted)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full"
                        style={{ width: `${pct}%`, background: meta.color }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Weighted forecast uses stage probabilities: Prospect 10% · Contacted 25% · Meeting 50% · Proposal 70% · Deal 85%.
        </p>
      </section>
    </div>
  );
}

function SummaryTile({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border bg-white p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-lg font-bold">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
